import { Row } from 'react-table';
import { getShortDate } from '../../../Utils/UtilFunctions';
import { Change } from '../Change';
import { GOLD_DATA, GRAM_WISE_CARAT_RATE } from '../types';
import { checkNumberSign } from '../utils';
import { addCommasToNumber } from '../../../Utils/CommonUtils';

const TableHeader = ({ text }: { text: string }): JSX.Element => {
  return <span className="text-base text-font-500 font-bold">{text}</span>;
};

interface COLUMN_TYPE {
  Header: string | JSX.Element;
  accessor: string;
  Cell: ({ row }: { row: Row<GOLD_DATA> }) => JSX.Element;
}

export const CITY_WISE_GOLD_COLUMN = (gram = 1): COLUMN_TYPE[] => [
  {
    Header: <TableHeader text="City" />,
    accessor: 'city',
    Cell: ({ row }) => {
      return (
        <span className="text-s-14 font-semibold capitalize">
          {row.original.city}
        </span>
      );
    },
  },
  {
    Header: <TableHeader text="22 Carat" />,
    accessor: 'carat22',
    Cell: ({ row }) => {
      const carat22 = row.original.carat22;
      return (
        <div className="flex gap-2 items-center text-s-14">
          <span>₹{addCommasToNumber(carat22.ratePerGram * gram)}</span>
          <Change
            sign={checkNumberSign(carat22.change)}
            change={carat22.change * gram}
            showInBrackets={true}
            isSmall={true}
          />
        </div>
      );
    },
  },
  {
    Header: <TableHeader text="24 Carat" />,
    accessor: 'carat24',
    Cell: ({ row }) => {
      const carat24 = row.original.carat24;
      return (
        <div className="flex gap-2 items-center text-s-14">
          <span>₹{addCommasToNumber(carat24.ratePerGram * gram)}</span>
          <Change
            sign={checkNumberSign(carat24.change)}
            change={carat24.change * gram}
            showInBrackets={true}
            isSmall={true}
          />
        </div>
      );
    },
  },
];

export const LAST_10_DAYS_GOLD_RATE_COLUMN = (gram = 1): COLUMN_TYPE[] => {
  return [
    {
      Header: <TableHeader text="Date" />,
      accessor: 'date',
      Cell: ({ row }) => {
        return (
          <span className="text-s-14 font-semibold capitalize">
            {getShortDate(row.original.date)}
          </span>
        );
      },
    },
    {
      Header: <TableHeader text="22 Carat" />,
      accessor: 'carat22',
      Cell: ({ row }) => {
        const carat22 = row.original.carat22;
        return (
          <div className="flex gap-2 items-center text-s-14">
            <span>₹{addCommasToNumber(carat22.ratePerGram * gram)}</span>
            <Change
              sign={checkNumberSign(carat22.change)}
              change={carat22.change * gram}
              showInBrackets={true}
              isSmall={true}
            />
          </div>
        );
      },
    },
    {
      Header: <TableHeader text="24 Carat" />,
      accessor: 'carat24',
      Cell: ({ row }) => {
        const carat24 = row.original.carat24;
        return (
          <div className="flex gap-2 items-center text-s-14">
            <span>₹{addCommasToNumber(carat24.ratePerGram * gram)}</span>
            <Change
              sign={checkNumberSign(carat24.change)}
              change={carat24.change * gram}
              showInBrackets={true}
              isSmall={true}
            />
          </div>
        );
      },
    },
  ];
};

export const GRAM_WISE_GOLD_COLUMN = [
  {
    Header: <TableHeader text="Carat" />,
    accessor: 'carat',
    Cell: ({ row }: { row: Row<GRAM_WISE_CARAT_RATE> }) => {
      return (
        <span className="text-s-14 font-semibold">{row.original.carat}</span>
      );
    },
  },
  {
    Header: <TableHeader text="1 g" />,
    accessor: 'rate*1',
    Cell: ({ row }: { row: Row<GRAM_WISE_CARAT_RATE> }) => {
      const rate = addCommasToNumber(Math.floor(row.original.rate * 1));
      const change = Math.floor(row.original.change * 1);

      return (
        <div className="flex gap-2 items-center text-s-12">
          <span className="text-s-14">₹{rate}</span>
          <Change
            sign={checkNumberSign(change)}
            change={change}
            showInBrackets={true}
            isSmall={true}
          />
        </div>
      );
    },
  },
  {
    Header: <TableHeader text="8 g" />,
    accessor: 'rate*8',
    Cell: ({ row }: { row: Row<GRAM_WISE_CARAT_RATE> }) => {
      const rate = addCommasToNumber(Math.floor(row.original.rate * 8));
      const change = Math.floor(row.original.change * 8);

      return (
        <div className="flex gap-2 items-center text-s-12">
          <span className="text-s-14">₹{rate}</span>
          <Change
            sign={checkNumberSign(change)}
            change={change}
            showInBrackets={true}
            isSmall={true}
          />
        </div>
      );
    },
  },
  {
    Header: <TableHeader text="10 g" />,
    accessor: 'rate*10',
    Cell: ({ row }: { row: Row<GRAM_WISE_CARAT_RATE> }) => {
      const rate = addCommasToNumber(Math.floor(row.original.rate * 10));
      const change = Math.floor(row.original.change * 10);

      return (
        <div className="flex gap-2 items-center text-s-12">
          <span className="text-s-14">₹{rate}</span>
          <Change
            sign={checkNumberSign(change)}
            change={change}
            showInBrackets={true}
            isSmall={true}
          />
        </div>
      );
    },
  },
  {
    Header: <TableHeader text="100 g" />,
    accessor: 'rate*100',
    Cell: ({ row }: { row: Row<GRAM_WISE_CARAT_RATE> }) => {
      const rate = addCommasToNumber(Math.floor(row.original.rate * 100));
      const change = Math.floor(row.original.change * 100);

      return (
        <div className="flex gap-2 items-center text-s-12">
          <span className="text-s-14">₹{rate}</span>
          <Change
            sign={checkNumberSign(change)}
            change={change}
            showInBrackets={true}
            isSmall={true}
          />
        </div>
      );
    },
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
] as any;
